/**
 * AgentDashboard - Review-First Home View
 * 
 * Inspired by Juicebox's agent-driven approach:
 * - Personalized greeting with stats
 * - Agent cards showing new lead counts (primary focus)
 * - EQ-curated recommended lists
 * - Compact search bar (accessible but secondary)
 * 
 * Philosophy: Value comes to you. Review, don't search.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Users,
  Building2,
  Briefcase,
  TrendingUp,
  Target,
  Sparkles,
  Play,
  Clock,
  Loader2,
  Search,
  Zap,
} from 'lucide-react';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useMediaQuery';

// Agent type configuration (people unifies candidate/client)
const AGENT_TYPES = {
  people: { icon: Users, color: 'bg-emerald-500', label: 'People' },
  candidate: { icon: Users, color: 'bg-emerald-500', label: 'Recruiting' },
  client: { icon: Target, color: 'bg-purple-500', label: 'Sales' },
  contact: { icon: Users, color: 'bg-blue-500', label: 'Contacts' },
  company: { icon: Building2, color: 'bg-indigo-500', label: 'Companies' },
  jobs: { icon: Briefcase, color: 'bg-orange-500', label: 'Jobs' },
  demand: { icon: TrendingUp, color: 'bg-red-500', label: 'Signals' },
  default: { icon: Sparkles, color: 'bg-gray-500', label: 'Agent' },
};

// =============================================================================
// THEMATIC IMAGES - Large pool of high-quality Unsplash images per category
// Each category has 6-8 images to minimize repetition
// =============================================================================
const THEMATIC_IMAGE_POOLS = {
  // Tech & Software
  software: [
    'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1537432376149-e84978a77ba2?w=800&h=400&fit=crop&q=80',
  ],
  engineer: [
    'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=400&fit=crop&q=80',
  ],
  developer: [
    'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1605379399642-870262d3d051?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1607799279861-4dd421887fb3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=800&h=400&fit=crop&q=80',
  ],
  tech: [
    'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=800&h=400&fit=crop&q=80',
  ],
  ai: [
    'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1676299081847-824916de030a?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1684369175833-4b445ad6bfb5?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1655720828018-edd2daec9349?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&h=400&fit=crop&q=80',
  ],
  data: [
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1543286386-713bdc3ce88d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1527474305487-b87b222841cc?w=800&h=400&fit=crop&q=80',
  ],
  cloud: [
    'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1560732488-6b0df240254a?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517483000871-1dbf64a6e1c6?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=800&h=400&fit=crop&q=80',
  ],
  
  // Design & Creative
  design: [
    'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1545235617-9465d2a55698?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1609921212029-bb5a28e60960?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=800&h=400&fit=crop&q=80',
  ],
  ux: [
    'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1576153192396-180ecef2a715?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1587440871875-191322ee64b0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=800&h=400&fit=crop&q=80',
  ],
  creative: [
    'https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1542744094-3a31f272c490?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1493119508027-2b584f234d6c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1456086272160-b28b0645b729?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=800&h=400&fit=crop&q=80',
  ],
  
  // Business & Sales
  sales: [
    'https://images.unsplash.com/photo-1556745757-8d76bdb6984b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop&q=80',
  ],
  marketing: [
    'https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1557838923-2985c318be48?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=800&h=400&fit=crop&q=80',
  ],
  business: [
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=400&fit=crop&q=80',
  ],
  executive: [
    'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?w=800&h=400&fit=crop&q=80',
  ],
  founder: [
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
  ],
  startup: [
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop&q=80',
  ],
  
  // Healthcare & Life Sciences
  healthcare: [
    'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551076805-e1869033e561?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop&q=80',
  ],
  medical: [
    'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=800&h=400&fit=crop&q=80',
  ],
  biotech: [
    'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1579154204601-01588f351e67?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507413245164-6160d8298b31?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1614935151651-0bea6508db6b?w=800&h=400&fit=crop&q=80',
  ],
  pharma: [
    'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=800&h=400&fit=crop&q=80',
  ],
  
  // Finance & Banking
  finance: [
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565373679580-fc0cb538f49b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop&q=80',
  ],
  fintech: [
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop&q=80',
  ],
  banking: [
    'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565514020179-026b92b2d70b?w=800&h=400&fit=crop&q=80',
  ],
  
  // HR & Recruiting
  hr: [
    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
  ],
  recruiter: [
    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1560439514-4e9645039924?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1573497019236-17f8177b81e8?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&h=400&fit=crop&q=80',
  ],
  talent: [
    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&q=80',
  ],
  
  // Product & Management
  product: [
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=400&fit=crop&q=80',
  ],
  manager: [
    'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1542626991-cbc4e32524cc?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1560439514-4e9645039924?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=400&fit=crop&q=80',
  ],
  
  // Industry specific
  manufacturing: [
    'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1537462715879-360eeb61a0ad?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1567789884554-0b844b597180?w=800&h=400&fit=crop&q=80',
  ],
  retail: [
    'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1528698827591-e19ccd7bc23d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=800&h=400&fit=crop&q=80',
  ],
  ecommerce: [
    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1557821552-17105176677c?w=800&h=400&fit=crop&q=80',
  ],
  logistics: [
    'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&h=400&fit=crop&q=80',
  ],
  real: [
    'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1560184897-ae75f418493e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565402170291-8491f14678db?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop&q=80',
  ],
  construction: [
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1565008447742-97f6f38c985c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?w=800&h=400&fit=crop&q=80',
  ],
  energy: [
    'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1548337138-e87d889cc369?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1558449028-b53a39d100fc?w=800&h=400&fit=crop&q=80',
  ],
  education: [
    'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=400&fit=crop&q=80',
  ],
  
  // Geographic
  europe: [
    'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1491557345352-5929e343eb89?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1493707553966-283afac8c358?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&h=400&fit=crop&q=80',
  ],
  london: [
    'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1505761671935-60b3a7427bad?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1533929736458-ca588d08c8be?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1520986606214-8b456906c813?w=800&h=400&fit=crop&q=80',
  ],
  york: [
    'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522083165195-3424ed129620?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1492666673288-3c4b4576ad9a?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1499092346589-b9b6be3e94b2?w=800&h=400&fit=crop&q=80',
  ],
  francisco: [
    'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1521747116042-5a810fda9664?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1506146332389-18140dc7b2fb?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1534050359320-02900022671e?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1541464522988-31b420f688b9?w=800&h=400&fit=crop&q=80',
  ],
  
  // Default fallbacks by type (also arrays)
  people: [
    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&q=80',
  ],
  candidate: [
    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop&q=80',
  ],
  client: [
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&h=400&fit=crop&q=80',
  ],
  company: [
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=400&fit=crop&q=80',
  ],
  default: [
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80',
  ],
};

// Simple hash function to get consistent but varied selection
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Get thematic image URL based on name/query keywords
// Uses hash of name to pick from pool for variety
const getThematicImage = (name, leadType) => {
  const searchName = name || '';
  const nameLower = searchName.toLowerCase();
  
  // Find matching category
  let matchedCategory = null;
  for (const keyword of Object.keys(THEMATIC_IMAGE_POOLS)) {
    if (nameLower.includes(keyword)) {
      matchedCategory = keyword;
      break;
    }
  }
  
  // Fallback to lead type or default
  if (!matchedCategory) {
    matchedCategory = leadType?.toLowerCase();
    if (!THEMATIC_IMAGE_POOLS[matchedCategory]) {
      matchedCategory = 'default';
    }
  }
  
  const imagePool = THEMATIC_IMAGE_POOLS[matchedCategory] || THEMATIC_IMAGE_POOLS.default;
  
  // Use hash of full name to pick image (consistent but varied)
  const hash = hashString(searchName || 'default');
  const imageIndex = hash % imagePool.length;
  
  return imagePool[imageIndex];
};

// =============================================================================
// INFER TYPE - Determine if search is for People (legacy: Candidates or Clients)
// Returns 'people', 'candidate', 'client', or null if uncertain (backward compat)
// =============================================================================
const inferSearchType = (item) => {
  // First check explicit lead_type if it's meaningful
  const leadType = item.lead_type?.toLowerCase();
  if (leadType === 'people') return 'people';
  if (leadType === 'candidate') return 'candidate';
  if (leadType === 'client' || leadType === 'company') return 'client';
  
  // Infer from name and query
  const searchText = `${item.name || ''} ${item.natural_query || ''}`.toLowerCase();
  
  // Client indicators - decision makers, executives, company-focused
  const clientKeywords = [
    'ceo', 'cfo', 'cto', 'coo', 'cmo', 'cio', 'chro',
    'chief', 'president', 'founder', 'owner', 'partner',
    'vp ', 'vice president', 'director', 'executive',
    'decision maker', 'c-level', 'c level', 'clevel',
    'companies', 'company', 'business', 'enterprise',
    'buyer', 'purchasing', 'procurement',
  ];
  
  // Candidate indicators - individual contributors, specific roles
  const candidateKeywords = [
    'engineer', 'developer', 'designer', 'analyst',
    'manager', 'specialist', 'coordinator', 'associate',
    'consultant', 'architect', 'scientist', 'researcher',
    'recruiter', 'talent', 'staff', 'senior', 'junior',
    'lead', 'principal', 'head of', 'hiring',
  ];
  
  // Check for client keywords first (typically more specific intent)
  for (const keyword of clientKeywords) {
    if (searchText.includes(keyword)) return 'client';
  }
  
  // Then check candidate keywords
  for (const keyword of candidateKeywords) {
    if (searchText.includes(keyword)) return 'candidate';
  }
  
  // Can't determine - return null
  return null;
};

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// =============================================================================
// HELPER: Get company logo URL
// =============================================================================
const getCompanyLogoUrl = (companyName, website) => {
  if (website) {
    const domain = website.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
    if (domain) return `https://logo.clearbit.com/${domain}`;
  }
  if (!companyName) return null;
  // Clean company name and guess domain
  const cleanName = companyName
    .replace(/\s+(Inc|LLC|Ltd|Corp|Co|Company|Group|Holdings)\.?$/i, '')
    .toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  if (cleanName.length > 2 && cleanName.length < 20) {
    return `https://logo.clearbit.com/${cleanName}.com`;
  }
  return null;
};

// =============================================================================
// AGENT CARD - Premium design with thematic header images
// =============================================================================
function AgentCard({ agent, onSelect, isLoading }) {
  const newLeadsCount = agent.new_leads_count || 0;
  const hasNewLeads = newLeadsCount > 0;
  const totalLeads = agent.leads_count || 0;
  const [imageError, setImageError] = React.useState(false);
  
  // Get thematic image based on agent name/query
  const thematicImage = getThematicImage(agent.name || agent.natural_query, agent.lead_type);
  
  // Extract tags from search criteria (concise, max 2)
  const extractTags = () => {
    const tags = [];
    if (agent.search_criteria?.jobTitles?.[0]) tags.push(agent.search_criteria.jobTitles[0]);
    else if (agent.search_criteria?.titles?.[0]) tags.push(agent.search_criteria.titles[0]);
    if (agent.search_criteria?.locations?.[0]) tags.push(agent.search_criteria.locations[0]);
    return tags.slice(0, 2);
  };
  const tags = extractTags();
  
  return (
    <button
      onClick={() => onSelect(agent)}
      disabled={isLoading}
      className={`group relative w-full bg-white rounded-2xl border overflow-hidden transition-all duration-300 text-left ${
        hasNewLeads
          ? 'border-[var(--eq-purple)]/20 shadow-lg hover:shadow-xl hover:border-[var(--eq-purple)]/40'
          : 'border-black/5 hover:border-black/10 hover:shadow-lg'
      } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
    >
      {/* Thematic Header Image */}
      <div className="relative h-28 overflow-hidden">
        {!imageError ? (
          <img 
            src={thematicImage}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[var(--eq-purple)]/20 to-[var(--eq-teal)]/20" />
        )}
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* New leads badge - premium pill style */}
        {hasNewLeads && (
          <div className="absolute top-3 right-3 px-2.5 py-1 bg-[var(--eq-purple)] text-white text-xs font-bold rounded-full shadow-lg">
            {newLeadsCount} new
          </div>
        )}
        
        {/* Lead count badge */}
        {totalLeads > 0 && !hasNewLeads && (
          <div className="absolute top-3 right-3 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full shadow-sm">
            <span className="text-xs font-bold text-[var(--eq-purple)]">{totalLeads} leads</span>
          </div>
        )}
      </div>
      
      {/* Content section - minimal and elegant */}
      <div className="p-4">
        {/* Title - Playfair Display */}
        <h3 
          className="text-base font-semibold text-black mb-1.5 group-hover:text-[var(--eq-purple)] transition-colors line-clamp-2 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {agent.name || 'Unnamed Alert'}
        </h3>
        
        {/* Tags - minimal pills */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((tag, idx) => (
              <span 
                key={idx}
                className="px-2 py-0.5 text-[10px] font-medium bg-[var(--eq-purple)]/5 text-[var(--eq-purple)] rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Stats row - subtle */}
        <div className="flex items-center justify-between pt-2 border-t border-black/5">
          <span className="text-xs text-black/40">
            {totalLeads > 0 ? `${totalLeads} leads` : 'No leads yet'}
          </span>
          <span className="flex items-center gap-1 text-xs font-medium text-[var(--eq-purple)] opacity-0 group-hover:opacity-100 transition-opacity">
            Review
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/90 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--eq-purple)]" />
        </div>
      )}
    </button>
  );
}

// =============================================================================
// SAVED LIST CARD - User's manual/saved searches (can be turned into alerts)
// =============================================================================
function SavedListCard({ list, onSelect, onActivate, isLoading }) {
  const totalLeads = list.leads_count || 0;
  const [imageError, setImageError] = React.useState(false);
  
  // Get thematic image based on list name/query
  const thematicImage = getThematicImage(list.name || list.natural_query, list.lead_type);
  
  return (
    <div 
      className={`group relative bg-white rounded-2xl border border-black/5 hover:border-[var(--eq-purple)]/20 hover:shadow-lg transition-all overflow-hidden ${isLoading ? 'opacity-50' : ''}`}
    >
      {/* Thematic Header Image */}
      <button
        onClick={() => onSelect(list)}
        disabled={isLoading}
        className="w-full text-left"
      >
        <div className="relative h-24 overflow-hidden">
          {!imageError ? (
            <img 
              src={thematicImage}
              alt=""
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[var(--eq-purple)]/20 to-[var(--eq-teal)]/20" />
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
          
          {/* Lead count badge */}
          {totalLeads > 0 && (
            <div className="absolute top-2 right-2 px-2 py-0.5 bg-white/95 backdrop-blur-sm rounded-full shadow-sm">
              <span className="text-[10px] font-bold text-[var(--eq-purple)]">{totalLeads} leads</span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-3">
          <h4 
            className="text-sm font-semibold text-black truncate group-hover:text-[#5a20ff] transition-colors"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {list.name || 'Unnamed List'}
          </h4>
          <p className="text-[10px] text-black/50 mt-0.5">
            Created {formatRelativeTime(list.created_at)}
          </p>
        </div>
      </button>
      
      {/* Turn into Alert action */}
      <div className="px-3 pb-3 pt-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onActivate(list);
          }}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-[#5a20ff] bg-[#5a20ff]/5 hover:bg-[#5a20ff] hover:text-white rounded-lg transition-all"
        >
          <Zap className="w-3.5 h-3.5" />
          Turn into Alert
        </button>
      </div>
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#5a20ff]" />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// RECOMMENDED LIST CARD - EQ-curated Discovery lists with thematic images
// =============================================================================
function RecommendedListCard({ list, onSubscribe }) {
  const [imageError, setImageError] = React.useState(false);
  
  // Get thematic image based on list name, description, or industry
  const thematicImage = getThematicImage(
    list.name || list.description || list.industry, 
    'client'
  );
  
  return (
    <div className="group bg-white rounded-2xl border border-black/5 overflow-hidden hover:border-[var(--eq-purple)]/20 hover:shadow-lg transition-all duration-300">
      {/* Thematic Header Image */}
      <div className="relative h-32 overflow-hidden">
        {!imageError ? (
          <img 
            src={thematicImage}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[var(--eq-purple)]/20 to-[var(--eq-teal)]/20" />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Industry tag on image */}
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 text-[10px] font-bold bg-white/95 backdrop-blur-sm text-[var(--eq-purple)] rounded-full shadow-sm">
            {list.industry || 'Curated'}
          </span>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h4 
          className="text-sm font-semibold text-black mb-1 group-hover:text-[var(--eq-purple)] transition-colors"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {list.name}
        </h4>
        <p className="text-xs text-black/50 line-clamp-2 mb-3">
          {list.description}
        </p>
        
        {/* Subscribe button */}
        <button
          onClick={() => onSubscribe(list)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium text-white bg-[var(--eq-purple)] hover:bg-[var(--eq-purple-dark)] rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
        >
          Subscribe
        </button>
      </div>
    </div>
  );
}


// =============================================================================
// MAIN AGENT DASHBOARD COMPONENT
// =============================================================================
// =============================================================================
// TAB FILTER COMPONENT - Clean segmented control
// =============================================================================
function TabFilter({ tabs, activeTab, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 bg-black/5 rounded-lg">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            activeTab === tab.id
              ? 'bg-white text-[#5a20ff] shadow-sm'
              : 'text-black/50 hover:text-black/70'
          }`}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className={`ml-1.5 ${activeTab === tab.id ? 'text-[#5a20ff]/60' : 'text-black/30'}`}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export default function AgentDashboard({ onSwitchToSearch, onSelectAgent: _onSelectAgent }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingAgentId, setLoadingAgentId] = useState(null);
  const [recommendedLists, setRecommendedLists] = useState([]);
  
  // Tab filter state
  const [listsFilter, setListsFilter] = useState('all'); // 'all' | 'candidate' | 'client'
  const [alertsFilter, setAlertsFilter] = useState('all'); // 'all' | 'candidate' | 'client'
  
  // Separate agents (is_alert=true, running autonomously) from lists (is_alert=false, manual searches)
  const activeAgents = agents.filter(a => a.is_alert === true);
  const savedLists = agents.filter(a => !a.is_alert);
  
  // Count by inferred type for tabs
  const listsPeopleCount = savedLists.filter(l => {
    const t = inferSearchType(l);
    return t === 'people' || t === 'candidate' || t === 'client';
  }).length;
  const listsCandidateCount = savedLists.filter(l => inferSearchType(l) === 'candidate').length;
  const listsClientCount = savedLists.filter(l => inferSearchType(l) === 'client').length;
  const alertsPeopleCount = activeAgents.filter(a => {
    const t = inferSearchType(a);
    return t === 'people' || t === 'candidate' || t === 'client';
  }).length;
  const alertsCandidateCount = activeAgents.filter(a => inferSearchType(a) === 'candidate').length;
  const alertsClientCount = activeAgents.filter(a => inferSearchType(a) === 'client').length;
  
  // Filter lists/alerts based on selected tab (uses inference)
  const filteredLists = savedLists.filter(list => {
    if (listsFilter === 'all') return true;
    if (listsFilter === 'people') return inferSearchType(list) === 'people' || inferSearchType(list) === 'candidate' || inferSearchType(list) === 'client';
    if (listsFilter === 'candidate') return inferSearchType(list) === 'candidate';
    if (listsFilter === 'client') return inferSearchType(list) === 'client';
    return true;
  });
  
  const filteredAlerts = activeAgents.filter(agent => {
    if (alertsFilter === 'all') return true;
    if (alertsFilter === 'people') return inferSearchType(agent) === 'people' || inferSearchType(agent) === 'candidate' || inferSearchType(agent) === 'client';
    if (alertsFilter === 'candidate') return inferSearchType(agent) === 'candidate';
    if (alertsFilter === 'client') return inferSearchType(agent) === 'client';
    return true;
  });
  
  // Calculate total new leads across all agents
  const totalNewLeads = activeAgents.reduce((sum, agent) => sum + (agent.new_leads_count || 0), 0);
  const agentsWithNewLeads = activeAgents.filter(a => (a.new_leads_count || 0) > 0).length;
  
  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/saved-searches');
      if (response.data?.savedSearches) {
        setAgents(response.data.savedSearches);
      }
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch recommended lists (EQ-curated)
  const fetchRecommendedLists = useCallback(async () => {
    // For now, use static recommended lists
    // TODO: Fetch from backend when API is ready
    setRecommendedLists([
      {
        id: 'weekly-ai-founders',
        name: 'Weekly AI Founder Digest',
        description: 'Founders at AI/ML startups who raised Series A+ in the last 30 days',
        industry: 'AI/ML',
        query: 'AI startup founders Series A funding last 30 days',
      },
      {
        id: 'fintech-sales-eu',
        name: 'FinTech Sales Leaders - EU',
        description: 'VP Sales and CROs at European fintech companies with 50+ employees',
        industry: 'FinTech',
        query: 'VP Sales CRO fintech Europe 50+ employees',
      },
      {
        id: 'healthcare-innovators',
        name: 'Healthcare Innovators Q1',
        description: 'Decision makers at healthcare companies with recent FDA approvals',
        industry: 'Healthcare',
        query: 'Healthcare executives FDA approval 2026',
      },
    ]);
  }, []);
  
  useEffect(() => {
    fetchAgents();
    fetchRecommendedLists();
  }, [fetchAgents, fetchRecommendedLists]);
  
  // Handle agent selection
  const handleSelectAgent = async (agent) => {
    setLoadingAgentId(agent.id);
    try {
      // Verify agent exists
      const response = await api.get(`/api/saved-searches/${agent.id}`);
      if (response.data?.success) {
        navigate(`/app/feed/${agent.id}`);
      }
    } catch (err) {
      console.error('Failed to load agent:', err);
    } finally {
      setLoadingAgentId(null);
    }
  };
  
  // Handle turning a saved list into an active agent
  const handleActivateList = async (list) => {
    setLoadingAgentId(list.id);
    try {
      // Update the saved search to be an alert
      await api.put(`/api/saved-searches/${list.id}`, {
        isAlert: true,
        alertFrequency: 'daily',
      });
      
      // Refresh the list to show updated state
      fetchAgents();
    } catch (err) {
      console.error('Failed to activate list:', err);
    } finally {
      setLoadingAgentId(null);
    }
  };
  
  // Handle subscribe to recommended list
  const handleSubscribeToList = async (list) => {
    // Create a new agent from the recommended list
    try {
      const response = await api.post('/api/saved-searches', {
        name: list.name,
        naturalQuery: list.query,
        searchCriteria: { query: list.query },
        leadType: 'contact',
        isAlert: true,
        alertFrequency: 'daily',
      });
      
      if (response.data?.savedSearch?.id) {
        // Refresh agents list
        fetchAgents();
        // Navigate to the new agent
        navigate(`/app/feed/${response.data.savedSearch.id}`);
      }
    } catch (err) {
      console.error('Failed to subscribe to list:', err);
    }
  };
  
  // Get first name for greeting
  const firstName = user?.name?.split(' ')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-[#f8f8f6]">
      {/* Background gradient */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#e8f5f0] via-[#f0f4ff] to-[#f5f0ff]" />
        <div className="absolute -top-1/4 -left-1/4 w-[60%] h-[60%] bg-[#52dcb3]/20 rounded-full blur-[120px]" />
        <div className="absolute top-1/4 -right-1/4 w-[50%] h-[70%] bg-[#5a20ff]/15 rounded-full blur-[100px]" />
      </div>
      
      {/* Content */}
      <div 
        className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto"
        style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)', marginTop: '56px' }}
      >
        {/* Hero Section - Greeting + Stats */}
        <div className="mb-8">
          <h1 
            className="text-3xl sm:text-4xl font-bold text-black mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {getGreeting()}, {firstName}.
          </h1>
          
          {totalNewLeads > 0 ? (
            <p className="text-lg text-black/60">
              You have <span className="text-[#5a20ff] font-semibold">{totalNewLeads} new leads</span> across {agentsWithNewLeads} agent{agentsWithNewLeads !== 1 ? 's' : ''} to review.
            </p>
          ) : agents.length > 0 ? (
            <p className="text-lg text-black/60">
              Your agents are up to date. Create a new search or check back later.
            </p>
          ) : (
            <p className="text-lg text-black/60">
              Let's set up your first agent to start finding leads automatically.
            </p>
          )}
          
          {/* Quick action buttons */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {totalNewLeads > 0 && (
              <button
                onClick={() => {
                  // Navigate to the agent with most new leads
                  const topAgent = [...activeAgents].sort((a, b) => (b.new_leads_count || 0) - (a.new_leads_count || 0))[0];
                  if (topAgent) handleSelectAgent(topAgent);
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#5a20ff] text-white font-medium rounded-full hover:bg-[#5a20ff]/90 transition-all shadow-lg shadow-[#5a20ff]/30"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <Play className="w-4 h-4" />
                Review All
              </button>
            )}
            <button
              onClick={onSwitchToSearch}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-black/10 text-black/70 font-medium rounded-full hover:border-[#5a20ff]/30 hover:text-[#5a20ff] transition-all"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <Search className="w-4 h-4" />
              New Search
            </button>
          </div>
        </div>
        
        
        {/* My Lists Section - Saved/manual searches (FIRST) */}
        {savedLists.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 
                  className="text-lg font-semibold text-black"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  My Lists
                </h2>
                <TabFilter
                  tabs={[
                    { id: 'all', label: 'All', count: savedLists.length },
                    { id: 'people', label: 'People', count: listsPeopleCount },
                    { id: 'candidate', label: 'Candidates', count: listsCandidateCount },
                    { id: 'client', label: 'Clients', count: listsClientCount },
                  ]}
                  activeTab={listsFilter}
                  onChange={setListsFilter}
                />
              </div>
              {filteredLists.length > 4 && (
                <button
                  onClick={() => navigate('/app/lists')}
                  className="text-sm text-black/50 hover:text-[#5a20ff] transition-colors"
                >
                  View all →
                </button>
              )}
            </div>
            
            {filteredLists.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredLists.slice(0, 4).map((list) => (
                  <SavedListCard
                    key={list.id}
                    list={list}
                    onSelect={handleSelectAgent}
                    onActivate={handleActivateList}
                    isLoading={loadingAgentId === list.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-black/40">
                No {listsFilter === 'people' ? 'people' : listsFilter === 'candidate' ? 'candidate' : 'client'} lists yet
              </div>
            )}
          </section>
        )}
        
        {/* My Alerts Section - Automated searches running autonomously (SECOND) */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 
                className="text-lg font-semibold text-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                My Alerts
              </h2>
              {activeAgents.length > 0 && (
                <TabFilter
                  tabs={[
                    { id: 'all', label: 'All', count: activeAgents.length },
                    { id: 'people', label: 'People', count: alertsPeopleCount },
                    { id: 'candidate', label: 'Candidates', count: alertsCandidateCount },
                    { id: 'client', label: 'Clients', count: alertsClientCount },
                  ]}
                  activeTab={alertsFilter}
                  onChange={setAlertsFilter}
                />
              )}
              <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-600 rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Running
              </span>
            </div>
            <div className="flex items-center gap-2">
              {filteredAlerts.length > 6 && (
                <button
                  onClick={() => navigate('/app/agents')}
                  className="text-sm text-black/50 hover:text-[#5a20ff] transition-colors"
                >
                  View all →
                </button>
              )}
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#5a20ff]" />
            </div>
          ) : filteredAlerts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredAlerts.slice(0, 6).map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  onSelect={handleSelectAgent}
                  isLoading={loadingAgentId === agent.id}
                />
              ))}
            </div>
          ) : activeAgents.length > 0 ? (
            <div className="text-center py-6 text-sm text-black/40">
              No {alertsFilter === 'people' ? 'people' : alertsFilter === 'candidate' ? 'candidate' : 'client'} alerts yet
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-2xl border border-black/5">
              <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[#5a20ff]/10 to-[#52dcb3]/10 flex items-center justify-center">
                <Bell className="w-7 h-7 text-[#5a20ff]" />
              </div>
              <h3 
                className="text-base font-semibold text-black mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                No alerts running
              </h3>
              <p className="text-sm text-black/50 mb-3 max-w-xs mx-auto">
                Alerts find new leads automatically. Turn a list into an alert or subscribe to a recommendation below.
              </p>
            </div>
          )}
        </section>
        
        {/* Discovery Section (THIRD/BOTTOM) */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 
                className="text-lg font-semibold text-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Discovery
              </h2>
            </div>
            <button className="text-sm text-black/50 hover:text-black transition-colors">
              See all →
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedLists.map((list) => (
              <RecommendedListCard
                key={list.id}
                list={list}
                onSubscribe={handleSubscribeToList}
              />
            ))}
          </div>
        </section>
        
        {/* Footer hint */}
        <div className="mt-12 text-center">
          <p className="text-[10px] text-black/20">
            Powered by <span className="font-semibold">EQ.app</span> • Press <kbd className="px-1.5 py-0.5 bg-black/5 rounded text-black/30 font-mono text-[9px]">?</kbd> for shortcuts
          </p>
        </div>
      </div>
    </div>
  );
}
